'use client';

import { c as _c } from "react/compiler-runtime";
import { truncateUtf8Text, utf8ByteLength } from '@morfi/contract-sdk';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { MarketImageDraft } from '@/components/images/market-picker';
import { MarketImagePickerModal } from '@/components/images/market-picker-modal';
import { OutcomeImageField } from '@/components/launchpad/outcome-image-field';
import { AppTooltip } from '@/components/ui/app-tooltip';
import type { FieldShellRenderArgs } from '@/components/ui/field-shell';
import { FieldShell } from '@/components/ui/field-shell';
import { Icon } from '@/components/ui/icon';
import { Copy01Icon, Delete02Icon } from '@/components/ui/icon/glyphs';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { useImageSearchPrefetch } from '@/hooks/launchpad/use-image-search-prefetch';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { buildOutcomeSearchQuery } from '@/lib/launchpad/outcome-search-query';
import { cn } from '@/lib/ui/utils';
interface OutcomeInputProps {
  index: number;
  label: string;
  ticker: string;
  /** Market ticker entered on step 1 — used as the live prefix for this outcome's ticker. */
  marketTicker: string;
  /** Market title entered on step 1 — passed to the image generator for prompt context. */
  marketTitle: string;
  imageUrl: string | null;
  onLabelChange: (label: string) => void;
  onTickerChange: (ticker: string) => void;
  /**
   * Deferred image lifecycle — `onImagePending` fires with the cropped local
   * File draft the moment the user confirms, and final upload happens at
   * create-time in compact market image order.
   */
  onImagePending: (draft: MarketImageDraft) => void;
  onImageReject: (error: Error) => void;
  /** Clear the slot — fires from the inline X button. */
  onImageRemove: () => void;
  onRemove: () => void;
  canRemove: boolean;
  error?: string | null;
  tickerError?: string | null;
  /** Marks the image as required and renders a persistent visible label. */
  imageRequired?: boolean;
  /** Visible image validation error connected to the upload control. */
  imageError?: string | null;
  /** AI generate flow: this outcome's label has arrived but its image fetch
   *  hasn't settled yet (no `outcome.locked` / `outcome.failed` event). When
   *  true and `imageUrl` is null, the slot renders a Skeleton instead of the
   *  empty dashed-upload affordance — that affordance would otherwise mislead
   *  the user into thinking they need to upload an image manually. */
  imageLoading?: boolean;
  /** Optional action(s) rendered inside the card, below the inputs. Used by
   *  the manual flow to surface the per-row "Generate image" CTA inside the
   *  same bordered container as the label/ticker inputs and image thumb. */
  footerSlot?: ReactNode;
  /** Perceived-speed opt-in for the AI generate flow: the outcome image is
   *  actively awaited in-view as it streams in, so load it eagerly at high
   *  priority (skip the lazy viewport gate). Default false — the manual flow
   *  keeps the standard lazy path. */
  eagerImage?: boolean;
  /** AI generate flow: inline preview data-URI for this outcome — the bytes
   *  the client DISPLAYS (zero network, never the gateway). Preferred over
   *  `imageUrl` for rendering; `imageUrl` (durable IPFS) is for submission. */
  imagePreview?: string;
  /** AI generate flow: the durable pin permanently failed — preview still
   *  displays, but the slot has no submittable URL, so flag it. */
  pinFailed?: boolean;
  /** Manual image-picker optimization. Disabled in AI generation because
   *  generated images are produced by the Lambda worker, not the picker search. */
  prefetchImageSearch?: boolean;
}
const STATIC_MAX_TICKER_LENGTH: number = PROPOSAL_FIELDS.outcomeTicker.maxLength;
const COMBINED_TICKER_MAX_LENGTH: number = PROPOSAL_FIELDS.tickerCombined.maxLength;

/**
 * Outcome ticker max shrinks as the market ticker grows so that the combined
 * ticker stays within the configured UTF-8 byte budget. The literal `X`
 * separator counts toward the budget — hence the `- 1` below.
 */
function computeOutcomeTickerMax(marketTickerLength: number): number {
  const remaining = COMBINED_TICKER_MAX_LENGTH - 1 - marketTickerLength;
  return Math.max(0, Math.min(STATIC_MAX_TICKER_LENGTH, remaining));
}
export function OutcomeInput(t0) {
  const $ = _c(75);
  const {
    index,
    label,
    ticker,
    marketTicker,
    marketTitle,
    imageUrl,
    onLabelChange,
    onTickerChange,
    onImagePending,
    onImageReject,
    onImageRemove,
    onRemove,
    canRemove,
    error,
    tickerError,
    imageRequired: t1,
    imageError,
    imageLoading: t2,
    footerSlot,
    eagerImage: t3,
    imagePreview,
    pinFailed: t4,
    prefetchImageSearch: t5
  } = t0;
  const imageRequired = t1 === undefined ? false : t1;
  const imageLoading = t2 === undefined ? false : t2;
  const eagerImage = t3 === undefined ? false : t3;
  const pinFailed = t4 === undefined ? false : t4;
  const prefetchImageSearch = t5 === undefined ? true : t5;
  const t = useTranslations("launchpad.form");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const isBlobUrl = imageUrl?.startsWith("blob:") ?? false;
  const nsfwLookup = useNsfwStatusByUrl(isBlobUrl ? null : imageUrl);
  const displaySrc = imagePreview !== undefined && imagePreview.length > 0 ? imagePreview : imageUrl;
  let t6;
  if ($[0] !== label || $[1] !== marketTitle) {
    t6 = buildOutcomeSearchQuery(marketTitle, label);
    $[0] = label;
    $[1] = marketTitle;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const t7 = prefetchImageSearch && !imageModalOpen;
  let t8;
  if ($[3] !== t7) {
    t8 = {
      enabled: t7
    };
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  useImageSearchPrefetch(t6, t8);
  let t9;
  if ($[5] !== marketTicker) {
    t9 = marketTicker.trim();
    $[5] = marketTicker;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const hasMarket = t9.length > 0;
  const tickerPrefix = hasMarket ? `$${marketTicker}` : "$";
  let t10;
  if ($[7] !== label || $[8] !== marketTicker) {
    const dynamicMaxTickerLength = computeOutcomeTickerMax(utf8ByteLength(marketTicker));
    t10 = truncateUtf8Text(label.replace(/\p{White_Space}+/gu, ""), dynamicMaxTickerLength);
    $[7] = label;
    $[8] = marketTicker;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const derivedTickerFromLabel = t10;
  const canApplyDerivedTicker = derivedTickerFromLabel.length > 0 && ticker.length === 0;
  let t11;
  if ($[10] !== onLabelChange) {
    t11 = function handleLabelChange(event) {
      onLabelChange(event.target.value);
    };
    $[10] = onLabelChange;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const handleLabelChange = t11;
  let t12;
  if ($[12] !== onTickerChange) {
    t12 = function handleTickerChange(event_0) {
      onTickerChange(event_0.target.value);
    };
    $[12] = onTickerChange;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const handleTickerChange = t12;
  let t13;
  if ($[14] !== canApplyDerivedTicker || $[15] !== derivedTickerFromLabel || $[16] !== onTickerChange) {
    t13 = function handleCopyLabelToTicker() {
      if (!canApplyDerivedTicker) {
        return;
      }
      onTickerChange(derivedTickerFromLabel);
    };
    $[14] = canApplyDerivedTicker;
    $[15] = derivedTickerFromLabel;
    $[16] = onTickerChange;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const handleCopyLabelToTicker = t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = function handleOpenImageModal() {
      setImageModalOpen(true);
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const handleOpenImageModal = t14;
  const tickerId = `outcome-ticker-${String(index)}`;
  const t15 = nsfwLookup.isNsfw;
  const t16 = `outcome-${String(index)}`;
  const t17 = index + 1;
  let t18;
  if ($[19] !== t || $[20] !== t17) {
    t18 = t("outcomeLabel", {
      number: t17
    });
    $[19] = t;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = error ?? undefined;
  let t20;
  if ($[22] !== canRemove || $[23] !== handleLabelChange || $[24] !== label || $[25] !== onRemove || $[26] !== t) {
    t20 = t21 => {
      const {
        controlProps
      } = t21;
      return <div className={cn("flex h-10 w-full min-w-0 items-center rounded-control border border-border-primary bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "focus-within:ring-2 focus-within:ring-border-primary focus-within:ring-offset-2", "hover:border-border-brand", controlProps["aria-invalid"] === true && "border-feedback-error-border focus-within:ring-feedback-error-border")}><input {...controlProps} type="text" value={label} onChange={handleLabelChange} placeholder={t("outcomePlaceholder")} className="min-w-0 flex-1 bg-transparent px-3 py-2 text-body-lg text-foreground-primary outline-none placeholder:text-foreground-tertiary focus-visible:outline-none md:text-body" />{canRemove ? <button type="button" onClick={onRemove} aria-label={t("removeOutcome")} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-feedback-error-content focus-visible:outline-none"><Icon icon={Delete02Icon} className="size-4" /></button> : <AppTooltip content={t("removeOutcomeDisabledTooltip")}><span className="inline-flex h-full"><button type="button" disabled={true} aria-label={t("removeOutcome")} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-tertiary"><Icon icon={Delete02Icon} className="size-4" /></button></span></AppTooltip>}</div>;
    };
    $[22] = canRemove;
    $[23] = handleLabelChange;
    $[24] = label;
    $[25] = onRemove;
    $[26] = t;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t16 || $[29] !== t18 || $[30] !== t19 || $[31] !== t20) {
    t21 = <FieldShell id={t16} label={t18} error={t19} labelClassName="sr-only">{t20}</FieldShell>;
    $[28] = t16;
    $[29] = t18;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t) {
    t22 = t("outcomeTickerLabel");
    $[33] = t;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const t23 = tickerError ?? undefined;
  let t24;
  if ($[35] !== canApplyDerivedTicker || $[36] !== handleCopyLabelToTicker || $[37] !== handleTickerChange || $[38] !== hasMarket || $[39] !== index || $[40] !== t || $[41] !== ticker || $[42] !== tickerPrefix) {
    t24 = t25 => {
      const {
        controlProps: controlProps_0
      } = t25;
      return <div className={cn("flex h-10 w-full min-w-0 items-center rounded-control border border-border-primary bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "focus-within:ring-2 focus-within:ring-border-primary focus-within:ring-offset-2", "hover:border-border-brand", controlProps_0["aria-invalid"] === true && "border-feedback-error-border focus-within:ring-feedback-error-border")}><span className="flex h-full shrink-0 items-center rounded-l-[calc(var(--radius-control)-1px)] border-r border-border-primary bg-background-tertiary/40 px-3 text-body font-medium tabular-nums text-foreground-primary">{tickerPrefix}</span>{hasMarket && <span aria-hidden="true" className="flex h-full shrink-0 items-center border-r border-border-primary bg-background-tertiary/40 px-2 text-body font-medium tabular-nums text-foreground-tertiary">X</span>}<input {...controlProps_0} type="text" value={ticker} onChange={handleTickerChange} placeholder={t("outcomeTickerPlaceholder")} className="min-w-0 flex-1 bg-transparent px-3 py-2 text-body-lg text-foreground-primary outline-none placeholder:text-foreground-tertiary focus-visible:outline-none md:text-body" />{canApplyDerivedTicker && <AppTooltip content={t("outcomeTickerUseLabel")}><button type="button" onClick={handleCopyLabelToTicker} aria-label={t("outcomeTickerUseLabel")} data-testid={`outcome-ticker-use-label-${String(index)}`} className="flex h-full shrink-0 items-center justify-center rounded-r-[calc(var(--radius-control)-1px)] border-l border-border-primary bg-background-tertiary/40 px-3 text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-foreground-primary focus-visible:outline-none"><Icon icon={Copy01Icon} className="size-4" /></button></AppTooltip>}</div>;
    };
    $[35] = canApplyDerivedTicker;
    $[36] = handleCopyLabelToTicker;
    $[37] = handleTickerChange;
    $[38] = hasMarket;
    $[39] = index;
    $[40] = t;
    $[41] = ticker;
    $[42] = tickerPrefix;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t22 || $[45] !== t23 || $[46] !== t24 || $[47] !== tickerId) {
    t25 = <FieldShell id={tickerId} label={t22} error={t23} labelClassName="sr-only">{t24}</FieldShell>;
    $[44] = t22;
    $[45] = t23;
    $[46] = t24;
    $[47] = tickerId;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t21 || $[50] !== t25) {
    t26 = <div className="flex min-w-0 flex-1 flex-col gap-3">{t21}{t25}</div>;
    $[49] = t21;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== displaySrc || $[53] !== eagerImage || $[54] !== imageError || $[55] !== imageLoading || $[56] !== imageRequired || $[57] !== index || $[58] !== nsfwLookup.isNsfw || $[59] !== onImageRemove || $[60] !== pinFailed || $[61] !== t26) {
    t27 = <OutcomeImageField index={index} displaySrc={displaySrc} isNsfw={t15} imageRequired={imageRequired} imageError={imageError} imageLoading={imageLoading} eagerImage={eagerImage} pinFailed={pinFailed} onOpen={handleOpenImageModal} onRemove={onImageRemove}>{t26}</OutcomeImageField>;
    $[52] = displaySrc;
    $[53] = eagerImage;
    $[54] = imageError;
    $[55] = imageLoading;
    $[56] = imageRequired;
    $[57] = index;
    $[58] = nsfwLookup.isNsfw;
    $[59] = onImageRemove;
    $[60] = pinFailed;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== footerSlot) {
    t28 = footerSlot !== undefined && footerSlot !== null ? <div className="flex justify-end">{footerSlot}</div> : null;
    $[63] = footerSlot;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== imageModalOpen || $[66] !== label || $[67] !== marketTitle || $[68] !== onImagePending || $[69] !== onImageReject) {
    t29 = <MarketImagePickerModal open={imageModalOpen} onOpenChange={setImageModalOpen} mode="outcome" marketTitle={marketTitle} outcomeLabel={label} onPending={onImagePending} onReject={onImageReject} />;
    $[65] = imageModalOpen;
    $[66] = label;
    $[67] = marketTitle;
    $[68] = onImagePending;
    $[69] = onImageReject;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t27 || $[72] !== t28 || $[73] !== t29) {
    t30 = <div className="flex flex-col gap-3 rounded-control border border-border-primary bg-background-primary p-3">{t27}{t28}{t29}</div>;
    $[71] = t27;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  return t30;
}